<script setup>
import {
  defineProps,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const hexToRgb = hex => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const rgbToHex = ([r, g, b]) =>
  '#' +
  [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');

const lerpColor = (fromHex, toHex, progressRatio) => {
  const fromRgb = hexToRgb(fromHex);
  const toRgb = hexToRgb(toHex);
  return rgbToHex(
    fromRgb.map(
      (channel, index) => channel + (toRgb[index] - channel) * progressRatio
    )
  );
};

const interpolateBubbleColor = (positionRatio, isDarkMode) => {
  const palette = isDarkMode
    ? ['#7d5fa8', '#6759a0', '#4f669b']
    : ['#fff1fb', '#f5efff', '#eef7ff'];

  if (positionRatio <= 0.5) {
    return lerpColor(palette[0], palette[1], positionRatio * 2);
  }

  return lerpColor(palette[1], palette[2], (positionRatio - 0.5) * 2);
};

const listRef = ref(null);
let scrollContainer = null;
let animationFrameId = null;
let themeObserver = null;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getScrollContainer = () =>
  listRef.value?.closest('.conversation-panel') || window;

const getViewportBounds = () => {
  if (!scrollContainer || scrollContainer === window) {
    return { top: 0, bottom: window.innerHeight, height: window.innerHeight };
  }

  const rect = scrollContainer.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom, height: rect.height };
};

const updateVisibleBubbleColors = () => {
  animationFrameId = null;

  if (!listRef.value) return;

  const viewport = getViewportBounds();
  const isDarkMode = document.documentElement.classList.contains('dark');
  const bubbles = listRef.value.querySelectorAll('.crow-outgoing-bubble');

  bubbles.forEach(bubble => {
    const rect = bubble.getBoundingClientRect();
    const isVisible =
      rect.bottom >= viewport.top && rect.top <= viewport.bottom;

    if (!isVisible) return;

    const centerY = rect.top + rect.height / 2;
    const positionRatio = clamp(
      (centerY - viewport.top) / Math.max(viewport.height, 1),
      0,
      1
    );

    bubble.style.setProperty(
      '--crow-outgoing-bubble-bg',
      interpolateBubbleColor(positionRatio, isDarkMode)
    );
  });
};

const scheduleBubbleColorUpdate = () => {
  if (animationFrameId) return;
  animationFrameId = window.requestAnimationFrame(updateVisibleBubbleColors);
};

const bindBubbleColorListeners = () => {
  scrollContainer = getScrollContainer();
  const listenerTarget = scrollContainer === window ? window : scrollContainer;
  listenerTarget.addEventListener('scroll', scheduleBubbleColorUpdate, {
    passive: true,
  });
  window.addEventListener('resize', scheduleBubbleColorUpdate, {
    passive: true,
  });

  themeObserver = new MutationObserver(scheduleBubbleColorUpdate);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
};

const unbindBubbleColorListeners = () => {
  const listenerTarget = scrollContainer === window ? window : scrollContainer;
  listenerTarget?.removeEventListener('scroll', scheduleBubbleColorUpdate);
  window.removeEventListener('resize', scheduleBubbleColorUpdate);
  themeObserver?.disconnect();
  themeObserver = null;

  if (animationFrameId) {
    window.cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
};

onMounted(() => {
  nextTick(() => {
    bindBubbleColorListeners();
    scheduleBubbleColorUpdate();
  });
});

onBeforeUnmount(unbindBubbleColorListeners);

watch(
  () => allMessages.value.map(message => message.id).join(','),
  () => nextTick(scheduleBubbleColorUpdate)
);

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul ref="listRef" class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
